import type { Item, ItemUsage, Part } from '../../domain/inventory';

/**
 * What "Active" really holds (tidaltech/koi#8447), by heartbeat: devices
 * reporting or within the 21-day grace period are in service; quiet longer,
 * gone quiet; never reported, never reported. Parts without telemetry and
 * site controllers (whose heartbeat isn't in item usage) can't be told
 * apart, so they're counted on their own. Replaced devices are no longer
 * Active at all, and shown next to it. Experimental is a count of Active
 * devices on a dual-deployment ("b") pen or B-twin site.
 */
export const HEARTBEAT_TYPES = [
  'CAMERA_SYSTEM',
  'WINCH_SYSTEM',
  'UNIT_CONTROLLER',
  'POWERBOX',
] as const;
export type HeartbeatType = (typeof HEARTBEAT_TYPES)[number];

/** Days without data before an Active device counts as gone quiet. */
export const GRACE_DAYS = 21;

export const activeRows = [
  'inService',
  'goneQuiet',
  'neverReported',
  'noTelemetry',
  'replaced',
] as const;
export type ActiveRow = (typeof activeRows)[number];

export const activeRowLabels: Record<ActiveRow, string> = {
  inService: `In service (data in the last ${GRACE_DAYS} days)`,
  goneQuiet: `Gone quiet (no data for over ${GRACE_DAYS} days)`,
  neverReported: 'Never reported',
  noTelemetry: 'No telemetry (passive parts, site controllers)',
  replaced: 'Replaced, awaiting return',
};

export type TypeCounts = Record<HeartbeatType | 'other', number> & { total: number };

export interface ActiveBreakdown {
  rows: Record<ActiveRow, TypeCounts>;
  experimental: TypeCounts;
}

function emptyCounts(): TypeCounts {
  return { total: 0, CAMERA_SYSTEM: 0, WINCH_SYSTEM: 0, UNIT_CONTROLLER: 0, POWERBOX: 0, other: 0 };
}

/** A dual-deployment pen ("M4b", "ST16b", "M22B") or a B-twin site code ("BSJB"). */
export function isDualDeployment(
  usage: ItemUsage | undefined,
  siteCode: string | undefined,
): boolean {
  if (usage?.lastPen && /\d[bB]$/.test(usage.lastPen)) return true;
  return Boolean(siteCode && siteCode.length === 4 && /[bB]$/.test(siteCode));
}

export function daysSince(day: string, today: Date): number {
  return Math.floor((today.getTime() - Date.parse(`${day}T00:00:00Z`)) / 86_400_000);
}

export function computeActiveBreakdown(
  items: readonly Item[],
  partsById: ReadonlyMap<string, Part>,
  usages: Readonly<Record<string, ItemUsage>>,
  siteCodeById: ReadonlyMap<string, string>,
  today: Date = new Date(),
): ActiveBreakdown {
  const rows = Object.fromEntries(activeRows.map((row) => [row, emptyCounts()])) as Record<
    ActiveRow,
    TypeCounts
  >;
  const experimental = emptyCounts();
  const add = (counts: TypeCounts, type: string) => {
    counts.total += 1;
    const key = (HEARTBEAT_TYPES as readonly string[]).includes(type)
      ? (type as HeartbeatType)
      : 'other';
    counts[key] += 1;
  };
  for (const item of items) {
    const type = partsById.get(item.partId)?.type ?? '';
    if (item.status === 'REPLACED') {
      add(rows.replaced, type);
      continue;
    }
    if (item.status !== 'ACTIVE') continue;
    const usage = usages[item.id];
    if (!(HEARTBEAT_TYPES as readonly string[]).includes(type)) {
      add(rows.noTelemetry, type);
      continue;
    }
    if (!usage?.lastUsed) add(rows.neverReported, type);
    else if (daysSince(usage.lastUsed, today) > GRACE_DAYS) add(rows.goneQuiet, type);
    else add(rows.inService, type);
    if (isDualDeployment(usage, siteCodeById.get(item.locationSiteId ?? item.siteId))) {
      add(experimental, type);
    }
  }
  return { rows, experimental };
}
